import type { CellViewWire } from '@badger-sentinel/protocol';

import { observationFor, trackStateOf, type MwTrackWire } from './mw.js';

/**
 * The display records `mw-map.js` draws — Phase 0's finding 1, for this phase.
 *
 * > *"The shell prepares display records; this element only draws them."*
 * > — `vendor/maps/mw-map.js`, first paragraph
 *
 * The vendored renderers import `./scenario.js` and `./mw.js` directly, so
 * vendoring them verbatim (M5-D7) means `model/` has to expose a **facade with
 * their shape**, not just a view model for React. This is that facade for the
 * warning picture: `CellViewWire` in, `api.setEvents(recs)` records out, nothing
 * else. Every field below is read by `drawEvents`, and no field is invented.
 *
 * ## What the wire cannot say, and why this returns nothing without geometry
 *
 * `drawEvents` calls `this.proj(r.at)` — a d3-geo projection over **lon/lat**.
 * `MwTrackView` carries no coordinates at all: `originSite` and `impactSite` are
 * `RegionId`s, and a region's shape is a client-side map pack keyed by scenario
 * (M5-D11), which Phase 5 ships. So this function takes the geometry as an
 * argument and returns an empty list when there is none.
 *
 * That is M5-D10 taken literally: a binding with no source is not defaulted. A
 * centroid guessed from a region name would put a launch in the sea and the
 * console would look like it was working.
 */

/** Region id → `[lon, lat]`, from the map pack. Phase 5's to supply. */
export type MwGeometry = Readonly<Record<string, readonly [number, number]>>;

/**
 * One launch, in the renderer's vocabulary.
 *
 * Field-for-field what `mw-map.js` reads: `at`/`to` are projected, `state`
 * decides colour and fill, `sel` decides declutter, `solid` tightens the
 * predicted impact area, `t0`/`tEnd` drive the flown-arc reveal, and `revised`
 * and `rv` are drawn only on the selected track.
 */
export interface MwMapRecord {
  readonly id: string;
  readonly at: readonly [number, number];
  readonly to: readonly [number, number] | null;
  readonly state: 'LIVE' | 'RELEASED' | 'DISMISSED';
  readonly sel: boolean;
  readonly orbital: boolean;
  /** The terminal stage has fired, so the predicted impact area is no longer a guess. */
  readonly solid: boolean;
  readonly rv: number;
  readonly revised: boolean;
  /** Seconds into the window at which this track's arc starts and ends. */
  readonly t0: number;
  readonly tEnd: number;
}

/**
 * The window a track's flight is drawn across, in corrected server time.
 *
 * `stepMs` rides with it because the projection only sends stage times that have
 * **not** fired yet — so a stage that has landed has no `atMs` anywhere on the
 * wire, and anything placing marks by lookup would draw only the pending ones.
 * With the step, every stage's instant is `fromMs + stage × stepMs`, observed or
 * not.
 */
export interface MwWindowSpan {
  readonly fromMs: number;
  readonly toMs: number;
  readonly stepMs: number;
}

function siteOf(track: MwTrackWire, field: 'originSite' | 'impactSite'): string | null {
  const observation = observationFor(track, field);
  // A dropped data point is *present* with no value: the seat was owed the origin
  // and did not get it, which is not the same as a launch that has no impact site.
  return observation?.value ?? null;
}

function rvOf(track: MwTrackWire): number {
  const observation = observationFor(track, 'rv');
  if (observation?.value == null) return 0;
  // The wire sends the band the way `boostDurationS` sends one (`1–3`). The map
  // fans one line per vehicle, so it draws the *least* the seat has been told.
  const first = /\d+/.exec(observation.value);
  return first === null ? 0 : Number(first[0]);
}

/**
 * One track's record, or `null` when its origin cannot be placed.
 *
 * A track with no drawable origin is **left off the map entirely** rather than
 * placed somewhere plausible. It is still on the rail, still selectable and still
 * actionable — the map is one view of the board and not the board.
 */
function recordFor(
  track: MwTrackWire,
  args: {
    readonly geometry: MwGeometry;
    readonly selected: string | null;
    readonly span: MwWindowSpan | null;
  },
): MwMapRecord | null {
  const origin = siteOf(track, 'originSite');
  const at = origin === null ? undefined : args.geometry[origin];
  if (at === undefined) return null;

  const impact = siteOf(track, 'impactSite');
  const to = impact === null ? null : (args.geometry[impact] ?? null);

  const span = args.span;
  const seconds = (ms: number): number => (span === null ? 0 : (ms - span.fromMs) / 1000);
  const scheduled = [...track.stageSchedule].sort((a, b) => a.atMs - b.atMs);
  const last = scheduled[scheduled.length - 1];

  return {
    id: track.eventId,
    at,
    to,
    state: trackStateOf(track),
    sel: args.selected === track.eventId,
    // No impact site and a terminal reading that names orbit: the ground track
    // does not come down. Read from the observation, never from the identity the
    // seat has asserted — the map must not draw the operator's own guess back at
    // them as though the sensors had confirmed it.
    orbital: to === null && track.revealedThroughStage >= 3,
    solid: track.revealedThroughStage >= 4,
    rv: rvOf(track),
    revised: false,
    t0: span === null ? 0 : seconds(span.fromMs),
    tEnd: span === null ? 1 : seconds(last?.atMs ?? span.toMs),
  };
}

/**
 * Every drawable track. Order is the projection's, which is sorted by event id.
 *
 * **There is no clock in a record, and this function must not take one.** The
 * renderer moves a track between ticks itself and `api.setClock` is its own
 * channel for exactly that reason — while `api.setEvents` rebuilds the whole
 * warning layer *and* the base map under it. A `serverNowMs` in the caller's
 * memo (which is where Phase 4 left one, harmlessly, while this returned nothing)
 * makes that rebuild fire once a second on a map that has not changed.
 */
export function mwMapRecords(
  view: CellViewWire | null,
  args: {
    readonly geometry: MwGeometry | null;
    readonly selected: string | null;
    readonly span: MwWindowSpan | null;
  },
): readonly MwMapRecord[] {
  const geometry = args.geometry;
  if (view === null || geometry === null) return [];
  const records: MwMapRecord[] = [];
  for (const track of view.mwTracks) {
    const record = recordFor(track, { ...args, geometry });
    if (record !== null) records.push(record);
  }
  return records;
}

/**
 * The clock the renderer interpolates against — `api.setClock(t, running)`.
 *
 * Seconds since the window opened, from **corrected** server time. The renderer
 * then runs its own `requestAnimationFrame` between ticks, which is a rendering
 * concern; what it must never do is decide *which* second it is.
 */
export function mwClockSeconds(span: MwWindowSpan | null, serverNowMs: number): number {
  if (span === null) return 0;
  return Math.max(0, (serverNowMs - span.fromMs) / 1000);
}
